import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/common/Tab';
import { observer } from 'mobx-react-lite';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useStore } from '@shared/hooks/useStore';
import { useSearchParams } from 'react-router-dom';
import { PublicationContainer } from '../components';
import { PUBLICATION_TYPE, PublicationType, TYPE_SEARCH, TypeSearch } from '@shared/types/enums';
import Pagination from '@shared/components/pagination/Pagination';
import { PUBLICATION_TAB_URL_TO_ENUM, PUBLICATION_TAB_ENUM_TO_URL } from '@shared/components/constants/common.constants';
import debounce from 'lodash.debounce';

export default observer(function FavoritesPage() {
    const { profileStore, apiStore } = useStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const isLoading = apiStore.isLoading;

    // Get tab from URL params, default to 'book'
    const urlTab = searchParams.get('tab') || 'book';
    const initialTab = PUBLICATION_TAB_URL_TO_ENUM[urlTab] || PUBLICATION_TYPE.Book;

    const [tab, setTab] = useState<string>(initialTab);
    const [currentPage, setCurrentPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [searchType, setSearchType] = useState<TypeSearch>(TYPE_SEARCH.OR);

    // Sync tab state with URL params on mount
    useEffect(() => {
        const urlTab = searchParams.get('tab') || 'book';
        const tabValue = PUBLICATION_TAB_URL_TO_ENUM[urlTab] || PUBLICATION_TYPE.Book;
        setTab(tabValue);
    }, [searchParams]);

    const fetchFavorites = useCallback((page: number, publicationType: PublicationType, search?: string, typeSearch?: string) => {
        profileStore.fetchMyFavorites(publicationType, page, search, 12, typeSearch);
    }, [profileStore]);

    // Debounced search function
    const debouncedSetSearch = useMemo(
        () => debounce((value: string) => {
            setDebouncedSearch(value.trim());
            setCurrentPage(1);
        }, 500),
        []
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [debouncedSetSearch]);

    const handleSearchChange = (value: string, typeSearch: TypeSearch) => {
        setSearchType(typeSearch);
        debouncedSetSearch(value);
    };

    const onTabChange = (value: string) => {
        setTab(value);
        setCurrentPage(1); // Reset to first page when tab changes
        // Reset search when tab changes to avoid stale search value
        debouncedSetSearch.cancel(); // Cancel any pending debounced search
        setDebouncedSearch(''); // Clear the debounced search value
        // Update URL query parameter
        const urlTabValue = PUBLICATION_TAB_ENUM_TO_URL[value as PublicationType] || 'book';
        setSearchParams({ tab: urlTabValue });
    };

    useEffect(() => {
        // Fetch favorites publications when page, tab, or debounced search changes
        fetchFavorites(currentPage, tab as PublicationType, debouncedSearch || undefined, searchType);
    }, [fetchFavorites, currentPage, tab, debouncedSearch, searchType]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Refresh and fetch favorites publications
    const refreshFavorites = () => {
        fetchFavorites(currentPage, tab as PublicationType, debouncedSearch || undefined, searchType);
    };

    const tabs = [
        {
            name: '書籍',
            value: PUBLICATION_TYPE.Book,
            content: (
                <>
                    <PublicationContainer
                        publications={profileStore.favoriteBooks}
                        isLoading={isLoading}
                        onToggleFavoriteSuccess={refreshFavorites}
                        onSearchChange={handleSearchChange}
                    />
                    {profileStore.totalFavoriteBooks > 0 && (
                        <div className='flex justify-center mt-6'>
                            <Pagination
                                type='full-pagination'
                                paginationClassName='w-fit'
                                currentPage={currentPage}
                                totalRecords={profileStore.totalFavoriteBooks}
                                pageSize={12}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )
        },
        {
            name: '雑誌',
            value: PUBLICATION_TYPE.MagazineIssue,
            content: (
                <>
                    <PublicationContainer
                        publications={profileStore.favoriteMagazines}
                        isLoading={isLoading}
                        onToggleFavoriteSuccess={refreshFavorites}
                        onSearchChange={handleSearchChange}
                    />
                    {profileStore.totalFavoriteMagazines > 0 && (
                        <div className='flex justify-center mt-6'>
                            <Pagination
                                type='full-pagination'
                                paginationClassName='w-fit'
                                currentPage={currentPage}
                                totalRecords={profileStore.totalFavoriteMagazines}
                                pageSize={12}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )
        }
    ];
    return (
        <section className='space-y-6 w-full'>
            <Tabs className='gap-4' value={tab} onValueChange={onTabChange}>
                <TabsList className='bg-background rounded-none border-b p-0 w-full h-12'>
                    {tabs.map(tab => (
                        <TabsTrigger
                            key={tab.value}
                            value={tab.value}
                            className='flex-1 text-base bg-background data-[state=active]:border-primary dark:data-[state=active]:border-primary h-full rounded-none border-0 border-b-2 border-transparent data-[state=active]:shadow-none'
                        >
                            {tab.name}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {tabs.map(tab => (
                    <TabsContent key={tab.value} value={tab.value}>
                        {tab.content}
                    </TabsContent>
                ))}
            </Tabs>
        </section>
    );
});
